import { useId } from 'react';

interface FormFieldProps {
    label: string;
    type?: string;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    required?: boolean;
    placeholder?: string;
    className?: string;
    autoFocus?: boolean;
    disabled?: boolean;
    helpText?: string;
}

/**
 * Accessible Form Field Component
 * 
 * Automatically handles:
 * - Label association with input
 * - Error messaging with ARIA attributes
 * - Required field indicators
 * - Help text descriptions
 * 
 * Usage:
 * <FormField
 *   label="Email Address"
 *   type="email"
 *   value={email}
 *   onChange={setEmail}
 *   error={emailError}
 *   required
 * />
 */
export function FormField({
    label,
    type = 'text',
    value,
    onChange,
    error,
    required = false,
    placeholder,
    className = '',
    autoFocus = false,
    disabled = false,
    helpText
}: FormFieldProps) {
    const inputId = useId();
    const errorId = `${inputId}-error`;
    const helpId = `${inputId}-help`;

    return (
        <div className={`form-group ${className}`}>
            <label
                htmlFor={inputId}
                className="label"
            >
                {label}
                {required && <span className="text-red-400 ml-1" aria-label="required">*</span>}
            </label>

            {helpText && (
                <p id={helpId} className="help">
                    {helpText}
                </p>
            )}

            <input
                id={inputId}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                required={required}
                autoFocus={autoFocus}
                disabled={disabled}
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={
                    error ? errorId : helpText ? helpId : undefined
                }
                className={`field ${error ? 'field-error' : ''}`}
            />

            {error && (
                <p
                    id={errorId}
                    role="alert"
                    className="error-text flex items-center gap-1"
                >
                    <span aria-hidden="true">⚠</span>
                    {error}
                </p>
            )}
        </div>
    );
}

interface TextAreaFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    required?: boolean;
    placeholder?: string;
    rows?: number;
    className?: string;
    helpText?: string;
}

/**
 * Accessible Textarea Field Component
 */
export function TextAreaField({
    label,
    value,
    onChange,
    error,
    required = false,
    placeholder,
    rows = 4,
    className = '',
    helpText
}: TextAreaFieldProps) {
    const textareaId = useId();
    const errorId = `${textareaId}-error`;
    const helpId = `${textareaId}-help`;

    return (
        <div className={`form-group ${className}`}>
            <label
                htmlFor={textareaId}
                className="label"
            >
                {label}
                {required && <span className="text-red-400 ml-1" aria-label="required">*</span>}
            </label>

            {helpText && (
                <p id={helpId} className="help">
                    {helpText}
                </p>
            )}

            <textarea
                id={textareaId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                required={required}
                rows={rows}
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={
                    error ? errorId : helpText ? helpId : undefined
                }
                className={`field ${error ? 'field-error' : ''}`}
            />

            {error && (
                <p
                    id={errorId}
                    role="alert"
                    className="error-text flex items-center gap-1"
                >
                    <span aria-hidden="true">⚠</span>
                    {error}
                </p>
            )}
        </div>
    );
}
